<template>
  <div
    class="offcanvas offcanvas-fullscreen-sm-down offcanvas-end pos-refund-offcanvas"
    tabindex="-1"
    id="offcanvasPOSRefund"
    ref="offcanvas"
    data-bs-backdrop="static"
    data-bs-scroll="true"
  >
    <!-- Header -->
    <div class="offcanvas-header bg-danger text-white py-3">
      <div>
        <h5 class="offcanvas-title fw-bold mb-0">
          <i class="fa-solid fa-rotate-left me-2"/>{{ $t('Refund Bill') }}
        </h5>
        <div class="small opacity-75 mt-1">#{{ bill.bill_no }}</div>
      </div>
      <button type="button" class="btn-close btn-close-white" @click="hide"/>
    </div>

    <!-- Body -->
    <div class="offcanvas-body p-3" style="background:#f7f9fc;">
      <!-- Summary -->
      <div class="bg-white rounded-3 shadow-sm p-3 mb-3 d-flex justify-content-between align-items-center">
        <div>
          <div class="text-muted small">{{ $t('Bill Total') }}</div>
          <div class="fs-5 fw-bold">{{ $storeIndex.formatThaiCurrency(billTotal) }}</div>
          <div v-if="alreadyRefunded > 0" class="small mt-1">
            <span class="text-muted">{{ $t('Already Refunded') }}:</span>
            <span class="text-danger fw-semibold ms-1">{{ $storeIndex.formatThaiCurrency(alreadyRefunded) }}</span>
          </div>
        </div>
        <i class="fa-solid fa-circle-exclamation fa-2x text-warning"/>
      </div>

      <!-- Items -->
      <div class="d-flex align-items-center justify-content-between mb-2 px-1">
        <span class="fw-bold text-dark">
          <i class="fa-solid fa-list me-2 text-muted"/>{{ $t('Select items and quantities to refund') }}
        </span>
        <button
          type="button"
          class="btn btn-sm btn-link text-danger p-0 text-decoration-none fw-semibold"
          :disabled="!hasRefundable"
          @click="toggleAll"
        >
          {{ allSelected ? $t('Clear') : $t('Select all') }}
        </button>
      </div>

      <div
        v-for="(row, i) in itemRows"
        :key="row.id"
        class="bg-white rounded-3 shadow-sm p-3 mb-2"
        :class="{ 'opacity-50': row.refundable === 0, 'pos-refund-item--active': row.qty > 0 }"
      >
        <div class="d-flex align-items-start gap-2">
          <div class="flex-grow-1" style="min-width:0;">
            <div class="fw-semibold text-truncate">{{ $ln(row) }}</div>
            <div class="text-muted small">
              {{ $storeIndex.formatThaiCurrency(row.unitPrice) }} × {{ row.orderedQuantity }}
            </div>
            <div class="d-flex gap-2 mt-1 flex-wrap">
              <span class="badge bg-light text-dark border">
                {{ $t('Refundable') }}: {{ row.refundable }}<template v-if="row.refundable < row.orderedQuantity"> / {{ row.orderedQuantity }}</template>
              </span>
              <span v-if="row.cancelledQuantity > 0" class="badge bg-danger-subtle text-danger-emphasis">
                {{ $t('Cancelled') }}: {{ row.cancelledQuantity }}
              </span>
              <span v-if="row.refundedQuantity > 0" class="badge bg-warning-subtle text-warning-emphasis">
                {{ $t('Refunded') }}: {{ row.refundedQuantity }}
              </span>
            </div>
          </div>
          <div class="text-end flex-shrink-0">
            <div class="fw-bold" :class="row.qty > 0 ? 'text-danger' : 'text-muted'">
              {{ $storeIndex.formatThaiCurrency(rowAmount(row)) }}
            </div>
          </div>
        </div>

        <div v-if="row.refundable > 0" class="d-flex align-items-center justify-content-end mt-2">
          <div class="custom-input-group input-group input-group-sm pos-refund-stepper flex-nowrap">
            <button
              type="button"
              class="input-group-text bg-white border-end-0 text-muted cursor-pointer"
              :disabled="row.qty <= 0"
              @click="setQty(i, row.qty - 1)"
            >
              <i class="fa-solid fa-minus"></i>
            </button>
            <input
              type="number"
              class="form-control border-start-0 border-end-0 text-center px-0"
              :value="row.qty"
              :min="0"
              :max="row.refundable"
              @input="setQty(i, Number($event.target.value))"
            >
            <button
              type="button"
              class="input-group-text bg-white border-start-0 text-muted cursor-pointer"
              :disabled="row.qty >= row.refundable"
              @click="setQty(i, row.qty + 1)"
            >
              <i class="fa-solid fa-plus"></i>
            </button>
          </div>
        </div>
      </div>

      <!-- Reason -->
      <div class="bg-white rounded-3 shadow-sm p-3 mt-3">
        <label class="form-label fw-semibold small">
          {{ $t('Refund Reason') }} <span class="text-danger">*</span>
        </label>
        <textarea
          class="form-control"
          rows="2"
          :placeholder="$t('Reason for refund') + '...'"
          v-model.trim="form.refund_reason"
          maxlength="500"
        />
      </div>

      <div class="text-muted small mt-2 px-1">
        {{ $t('Includes proportional bill discount, service charge, and VAT based on item share of the bill.') }}
      </div>
    </div>

    <!-- Footer -->
    <div class="bg-white p-3 border-top">
      <div class="d-flex justify-content-between align-items-center mb-2">
        <span class="fw-semibold">{{ $t('Total Refund') }}</span>
        <span class="fs-4 fw-bold text-danger">{{ $storeIndex.formatThaiCurrency(totalRefundAmount) }}</span>
      </div>
      <div class="d-flex gap-2">
        <button type="button" class="btn btn-outline-secondary rounded-3 px-4 flex-fill" :disabled="submitting" @click="hide">
          {{ $t('Cancel') }}
        </button>
        <button
          type="button"
          class="btn btn-danger rounded-3 px-4 fw-bold flex-fill"
          :disabled="!canConfirm || submitting"
          @click="confirm"
        >
          <i v-if="submitting" class="fa-solid fa-spinner fa-spin me-2"/>
          <i v-else class="fa-solid fa-rotate-left me-2"/>
          {{ $t('Confirm Refund') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'POSBillOffcanvasRefund',
  props: {
    bill: { type: Object, required: true }
  },
  emits: ['close', 'confirm'],
  data() {
    return {
      submitting: false,
      itemRows: [],
      form: {
        refund_reason: ''
      }
    };
  },
  computed: {
    billTotal() {
      return this.toNumber(this.bill.total);
    },
    billSubtotal() {
      return this.toNumber(this.bill.sub_total);
    },
    alreadyRefunded() {
      return this.toNumber(this.bill.refunded_amount);
    },
    totalRefundAmount() {
      if (this.billSubtotal <= 0) return 0;
      return this.itemRows.reduce((sum, row) => sum + this.rowAmount(row), 0);
    },
    selectedItems() {
      return this.itemRows
        .filter(r => r.qty > 0)
        .map(r => ({ id: r.id, quantity: r.qty }));
    },
    canConfirm() {
      return this.selectedItems.length > 0
        && (this.form.refund_reason || '').trim().length > 0;
    },
    hasRefundable() {
      return this.itemRows.some(r => r.refundable > 0);
    },
    allSelected() {
      const refundables = this.itemRows.filter(r => r.refundable > 0);
      return refundables.length > 0 && refundables.every(r => r.qty >= r.refundable);
    }
  },
  mounted() {
    this.itemRows = this.buildItemRows();
    $(this.$refs.offcanvas).offcanvas('show');
    $(this.$refs.offcanvas).on('hidden.bs.offcanvas', () => this.$emit('close'));
  },
  methods: {
    toNumber(v) {
      const n = parseFloat(v);
      return Number.isFinite(n) ? n : 0;
    },
    buildItemRows() {
      return (this.bill.items || []).map(it => {
        const ordered   = this.toNumber(it.quantity);
        const cancelled = this.toNumber(it.cancelled_quantity);
        const refunded  = this.toNumber(it.refunded_quantity);
        const refundable = Math.max(0, ordered - cancelled - refunded);
        return {
          id: it.id,
          name: it.name,
          name_en: it.name_en,
          unitPrice: this.toNumber(it.unit_price),
          total: this.toNumber(it.total),
          orderedQuantity: ordered,
          cancelledQuantity: cancelled,
          refundedQuantity: refunded,
          refundable,
          qty: 0
        };
      });
    },
    // Proportional refund — distributes bill-level discount/service/VAT across the
    // item by share of subtotal:
    //   refund = (item.total / bill.sub_total) × bill.total × (refund_qty / ordered_qty)
    rowAmount(row) {
      if (row.orderedQuantity <= 0 || this.billSubtotal <= 0) return 0;
      const itemShare = row.total / this.billSubtotal;
      return itemShare * this.billTotal * (row.qty / row.orderedQuantity);
    },
    setQty(idx, q) {
      const row = this.itemRows[idx];
      const clamped = Math.max(0, Math.min(row.refundable, Number(q) || 0));
      this.itemRows[idx] = { ...row, qty: clamped };
    },
    toggleAll() {
      const select = !this.allSelected;
      this.itemRows = this.itemRows.map(r => ({ ...r, qty: select ? r.refundable : 0 }));
    },
    async confirm() {
      if (!this.canConfirm || this.submitting) return;
      this.submitting = true;
      this.$emit('confirm', {
        refund_reason: this.form.refund_reason.trim(),
        items: this.selectedItems
      });
    },
    hide() {
      $(this.$refs.offcanvas).offcanvas('hide');
    },
    stopSubmitting() {
      this.submitting = false;
    }
  }
};
</script>

<style scoped>
.pos-refund-offcanvas {
  width: 560px;
  max-width: 100%;
}
.pos-refund-item--active {
  outline: 2px solid #dc3545;
  outline-offset: -1px;
}
.pos-refund-stepper {
  width: 130px;
}
.pos-refund-stepper .input-group-text {
  padding-left: 0.5rem;
  padding-right: 0.5rem;
}
.pos-refund-stepper .input-group-text:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
/* ซ่อนลูกศร spinner ของ input[type=number] ให้เหลือแต่ปุ่ม +/- */
.pos-refund-stepper input[type='number'] {
  -moz-appearance: textfield;
  appearance: textfield;
}
.pos-refund-stepper input[type='number']::-webkit-outer-spin-button,
.pos-refund-stepper input[type='number']::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
</style>
